import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  ExternalLink,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import { Menu as LucideMenu, HelpCircle, Map as MapIcon, MapPin } from 'lucide-react';

// Coachify Marketplace (this app) <-> Coachify Maps (the discovery/map app)
// cross-link — mirrors the logo-click link Maps' own header carries back
// here. Same account/database on both sides, kept as separate, explicitly
// switched experiences. ?dashboard=1 opens Maps' own dashboard sheet
// directly for a signed-in coach (coachify-app.tsx's matching deep-link
// effect) -- a no-op if they're not signed in there, so safe to always
// include rather than needing to know their Maps auth state from here.
const COACHIFY_MAPS_URL = 'https://discovery.coachifyusa.com/?dashboard=1';

// The four-question, ~30 second version of getting listed -- no Stripe, no
// bookable listing, just a pin. Someone who lands on the Marketplace first
// shouldn't have to find the campaign link to get it, and "Become a Coach"
// next to this is the full Stripe-connected path, not this one. Providers
// who already have a bookable listing get the in-profile "Get Pinned on the
// Map" flow instead, so this is hidden for them.
const COACHIFY_MAPS_JOIN_URL = 'https://discovery.coachifyusa.com/join';

// Identity-unification Phase 3: one-click handoff into the matching
// Discovery/Maps dashboard, reusing the exact same Supabase account this
// person is already linked to (see server/api/auth/discoveryHandoff.js) --
// not a plain hyperlink like COACHIFY_MAPS_URL above, since it needs a
// live session to mint the handoff token against. Mobile menu has its own
// copy of this, same as COACHIFY_MAPS_URL's split above.
async function openDiscoveryDashboard() {
  try {
    const res = await fetch('/api/coachify-sso/discovery-handoff?target=%2F%3Fdashboard%3D1', {
      credentials: 'include',
    });
    const data = await res.json();
    if (data?.redirectUrl) {
      window.location.href = data.redirectUrl;
    }
  } catch (err) {
    console.error('Failed to open Discovery dashboard', err); // eslint-disable-line no-console
  }
}

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { search } from '../../../../config/configMaps';
import useAdminAccess from '../../../../hooks/useAdminAccess';
import { useTheme } from '../../../../context/themeContext';
import ThemeToggle from '../../../LandingPage/Components/ThemeToggle/ThemeToggle';

// SignupLink, LoginLink and InboxLink are handled within ProfileMenu

const ProfileMenu = ({ currentUser, onLogout, isAdmin }) => {
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.userMenuPill}>
          <LucideMenu size={18} className={css.hamburgerIcon} />
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {!currentUser && (
          <>
            <MenuItem key="SignupPage">
              <NamedLink name="SignupPage" className={css.menuLink}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.signup" />
              </NamedLink>
            </MenuItem>
            <MenuItem key="LoginPage">
              <NamedLink name="LoginPage" className={css.menuLink}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.login" />
              </NamedLink>
            </MenuItem>
            <hr className={css.menuDivider} />
          </>
        )}
        {currentUser && (
          <>
            <hr className={css.menuDivider} />
            {currentUser?.attributes?.profile?.publicData?.coach && (
              <MenuItem key="ProviderPage">
                <NamedLink name="ProviderPage" className={css.menuLink}>
                  <span className={css.menuItemBorder} />
                  <FormattedMessage
                    id="TopbarDesktop.providerPage"
                    defaultMessage="Provider Dashboard"
                  />
                </NamedLink>
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem key="AdminDashboard">
                <NamedLink name="AdminDashboard" className={css.menuLink}>
                  <span className={css.menuItemBorder} />
                  <FormattedMessage
                    id="TopbarDesktop.adminDashboard"
                    defaultMessage="Admin Dashboard"
                  />
                </NamedLink>
              </MenuItem>
            )}
            <MenuItem key="CustomerPage">
              <NamedLink name="CustomerPage" className={css.menuLink}>
                <span className={css.menuItemBorder} />
                <FormattedMessage
                  id="TopbarDesktop.customerPage"
                  defaultMessage="Customer Dashboard"
                />
              </NamedLink>
            </MenuItem>
            <MenuItem key="DiscoveryDashboard">
              <InlineTextButton rootClassName={css.menuLink} onClick={openDiscoveryDashboard}>
                <span className={css.menuItemBorder} />
                {currentUser?.attributes?.profile?.publicData?.coach
                  ? 'Coaches Dashboard (Maps)'
                  : 'Customer Dashboard (Maps)'}
              </InlineTextButton>
            </MenuItem>
            <hr className={css.menuDivider} />
            <MenuItem key="ProfileSettingsPage">
              <NamedLink name="ProfileSettingsPage" className={css.menuLink}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
              </NamedLink>
            </MenuItem>
            <MenuItem key="AccountSettingsPage">
              <NamedLink name="AccountSettingsPage" className={css.menuLink}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
              </NamedLink>
            </MenuItem>
            <MenuItem key="logout">
              <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.logout" />
              </InlineTextButton>
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    onToggleBecomeCoachModal,
    onOpenSupportModal,
  } = props;
  const [mounted, setMounted] = useState(false);
  const { isAdmin } = useAdminAccess(currentUser);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = true;
  const classes = classNames(rootClassName || css.root, className);

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      key={props.searchFormKey}
      className={css.searchForm}
      onSubmit={onSearchSubmit}
      onDateRangeChange={props.onDateRangeChange}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : null;

  return (
    <nav className={classes}>
      <div className={css.logoArea}>
        <LinkedLogo className={css.logoLink} layout="desktop" />
      </div>

      <div className={css.searchArea}>{searchFormMaybe}</div>

      <div className={css.rightMenu}>
        <ExternalLink
          href={COACHIFY_MAPS_URL}
          className={css.mapsLink}
          title="Coachify Maps"
          aria-label="Coachify Maps"
          target="_self"
        >
          <MapIcon size={14} />
          Maps
        </ExternalLink>
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        {(!currentUser || (currentUser && !currentUser?.attributes?.profile?.publicData?.coach)) && (
          <>
            <ExternalLink
              href={COACHIFY_MAPS_JOIN_URL}
              className={css.joinMapLink}
              title="Get on the Coachify map -- free, about 30 seconds"
              target="_self"
            >
              <MapPin size={14} />
              Get on the map
            </ExternalLink>
            <InlineTextButton
              className={css.becomeCoachButton}
              onClick={() => onToggleBecomeCoachModal(true)}
            >
              Become a Coach
            </InlineTextButton>
          </>
        )}
        {currentUser && onOpenSupportModal ? (
          <button
            type="button"
            className={css.supportButton}
            onClick={onOpenSupportModal}
            aria-label="Support"
            title="Support"
          >
            <HelpCircle size={20} />
          </button>
        ) : null}
        {(authenticatedOnClientSide || !isAuthenticatedOrJustHydrated) && (
          <ProfileMenu currentUser={currentUser} onLogout={onLogout} isAdmin={isAdmin} />
        )}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
